export function capitalize<T extends string | null | undefined>(str: T): T {
	if (!str) {
		return str;
	}

	return `${str.charAt(0).toUpperCase()}${str.slice(1)}` as T;
}

export function lowerFirst<T extends string | null | undefined>(str: T): T {
	if (!str) {
		return str;
	}

	return `${str.charAt(0).toLowerCase()}${str.slice(1)}` as T;
}

export function equalStrings(actual: string, expected: string): boolean {
	return actual === expected;
}

export function getBreadcrumbName(route: string): string {
	return route
		.replaceAll('-', ' ')
		.split(' ')
		.map(capitalize)
		.join(' ');
}

export function getBreadcrumbs(route: string, lookup: Record<string, string> = {}): Record<string, string> {
	const routeMap: Record<string, string> = {};
	const splitRoute = route.split('/').filter((v: string) => v.length > 0);

	for (let i = 0; i < splitRoute.length; i += 1) {
		const currentRoute = splitRoute[ i ];
		const sliceRoutes = splitRoute.slice(0, i + 1);
		const capitalizedRoute = getBreadcrumbName(lookup[ currentRoute ] ?? currentRoute);
		const routesJoin = sliceRoutes.join('/');
		const routeWithSlashPrepended = `/${routesJoin}`;

		routeMap[ routeWithSlashPrepended ] = capitalizedRoute;
	}

	return routeMap;
}

export function getRandomString(length: number): string {
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890';
	let randomString = '';

	for (let i = 0; i < length; i++) {
		const character = characters.charAt(Math.floor(Math.random() * characters.length));

		randomString += character;
	}

	return randomString;
}

export function makeSnakeCase(arr: Array<string>): string {
	return arr.join('_');
}

export function stringContainsSubstringCaseInsensitive(string: string, subString: string): boolean {
	return string.toLowerCase().includes(subString.toLowerCase());
}

export function stringValue(str: string | undefined | null): string {
	return str ? str : '';
}

export function getDisplayUrl(url: string): string {
	const protocolRegex = /https?:\/\//iu;

	return url.replace(protocolRegex, '');
}

export function getFullName(firstName: string | undefined | null, lastName: string | undefined | null): string {
	return `${stringValue(firstName)} ${stringValue(lastName)}`.trim();
}

export function getInitials(firstName: string | undefined | null, lastName: string | undefined | null): string {
	const firstInitial = stringValue(firstName).charAt(0)
		.toUpperCase();
	const secondInitial = stringValue(firstName).charAt(1)
		.toLowerCase();
	const lastInitial = stringValue(lastName).charAt(0)
		.toUpperCase();

	return `${firstInitial}${lastInitial ? lastInitial : secondInitial}`;
}

export enum TransferProtocol {
	Http = 'http://',
	Https = 'https://'
}

export function makeValidUrl(str: string, defaultProcotol: TransferProtocol = TransferProtocol.Http): string {
	let url;

	try {
		url = new URL(str);
	} catch {
		return `${defaultProcotol}${str}`;
	}

	return url.protocol && str;
}

export function removeQuotes(str: string): string {
	return str.replace(/^"(.+(?="$))"$/u, '$1');
}

export function getFilenameFromContentDisposition(contentDisposition: string): string {
	const start = 'attachment; filename=';
	const end = '; filename*';

	const filename = contentDisposition.substring(
		contentDisposition.indexOf(start) + start.length,
		contentDisposition.lastIndexOf(end)
	);

	return removeQuotes(filename);
}

export function isJSON(input: string): boolean {
	const rxOne = /^[\],:{}\s]*$/u;
	const rxTwo = /\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4})/gu;
	const rxThree = /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/gu;
	const rxFour = /(?:^|:|,)(?:\s*\[)+/gu;

	return Boolean(input.length) && rxOne.test(input.replace(rxTwo, '@').replace(rxThree, ']')
		.replace(rxFour, ''));
}

export function jsonSafeParse<T>(json: string): T | undefined {
	try {
		return JSON.parse(json);
	} catch {
		return undefined;
	}
}

export function getTitleWithCount(title: string, count: number | null | undefined): string {
	return count ? `${title} (${count})` : title;
}

/**
 * Masks a string by showing a specified number of characters at the start and end,
 * with a mask character repeated in between.
 *
 * @param value - The string to mask
 * @param startChars - Number of characters to show at the start
 * @param endChars - Number of characters to show at the end
 * @param maskChar - Character to use for masking (default: '•')
 * @returns The masked string, or the original string if it's shorter than startChars + endChars
 *
 * @example
 * maskString('dd_live_sk_qk1234567890jjo0', 20, 4) // 'dd_live_sk_qk•••••••••••••••••••••••jjo0'
 * maskString('1234567890', 3, 3) // '123••••890'
 * maskString('short', 10, 5) // 'short' (too short to mask)
 */
export function maskString(
	value: string,
	startChars: number,
	endChars: number,
	maskChar: string = '•'
): string {
	const minLength = startChars + endChars;

	// If the string is too short to mask meaningfully, return it as-is
	if (value.length < minLength) {
		return value;
	}

	const middleLength = value.length - startChars - endChars;
	const start = value.substring(0, startChars);
	const end = value.substring(value.length - endChars);

	return `${start}${maskChar.repeat(middleLength)}${end}`;
}
